// src/context/ScreeningContext.jsx - Version Appwrite

import React, { createContext, useContext, useReducer } from 'react'
import * as pdfjsLib from 'pdfjs-dist/webpack'

if (typeof window !== 'undefined') {
  pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js`
}

const ScreeningContext = createContext()

const initialState = {
  currentStep: 1,
  totalSteps: 9,
  currentSessionId: null,
  uploadedFiles: [],
  selectedAIModel: 'claude-sonnet-3.5',
  criteria: {
    selectedProfile: '',
    profileDescription: '',
    
    // Critères disqualifiants
    disqualifying_no_photo: false,
    disqualifying_spelling_errors: false,
    disqualifying_too_long: false,
    disqualifying_no_contact: false,
    disqualifying_no_experience: false,
    
    // Critères de tri
    ageImportant: false,
    ageMin: '',
    ageMax: '',
    genderImportant: false,
    preferredGender: 'indifferent',
    
    experienceImportant: false,
    expMin: '',
    expMax: '',
    experienceType: 'any',
    
    educationImportant: false,
    minEducationLevel: '',
    educationField: '',
    
    companiesCountImportant: false,
    minCompaniesCount: '',
    maxCompaniesCount: '',
    
    languagesImportant: false,
    selectedLanguages: [],
    
    technicalCriteria: {},
    customTechnicalSkills: ''
  },
  analysisResults: [],
  analyzing: false,
  progress: {
    current: 0,
    total: 0,
    success: 0,
    errors: 0
  }
}

const screeningReducer = (state, action) => {
  switch (action.type) {
    case 'SET_STEP':
      return { ...state, currentStep: action.payload }
    
    case 'NEXT_STEP':
      return { 
        ...state, 
        currentStep: Math.min(state.currentStep + 1, state.totalSteps) 
      }
    
    case 'PREV_STEP':
      return { 
        ...state, 
        currentStep: Math.max(state.currentStep - 1, 1) 
      }
    
    case 'SET_SESSION_ID':
      return { ...state, currentSessionId: action.payload }
    
    case 'SET_UPLOADED_FILES':
      return { ...state, uploadedFiles: action.payload }
    
    case 'ADD_FILES':
      const newFiles = action.payload.filter(file => 
        !state.uploadedFiles.some(existingFile => existingFile.file_name === file.file_name)
      )
      return { ...state, uploadedFiles: [...state.uploadedFiles, ...newFiles] }
    
    case 'REMOVE_FILE':
      return { 
        ...state, 
        uploadedFiles: state.uploadedFiles.filter(file => file.id !== action.payload) 
      }
    
    case 'UPDATE_CRITERIA':
      return { 
        ...state, 
        criteria: { 
          ...state.criteria, 
          [action.field]: action.value 
        } 
      }
    
    case 'SET_AI_MODEL':
      return { ...state, selectedAIModel: action.payload }
    
    case 'SET_ANALYZING':
      return { ...state, analyzing: action.payload }
    
    case 'UPDATE_PROGRESS':
      return { ...state, progress: action.payload }
    
    case 'SET_RESULTS':
      return { ...state, analysisResults: action.payload, analyzing: false }
    
    case 'RESET_SCREENING':
      return {
        ...initialState,
        currentStep: 1
      }
    
    default:
      return state
  }
}

export function ScreeningProvider({ children }) {
  const [state, dispatch] = useReducer(screeningReducer, initialState)

  const setStep = (step) => {
    dispatch({ type: 'SET_STEP', payload: step })
  }

  const nextStep = () => {
    dispatch({ type: 'NEXT_STEP' })
  }

  const prevStep = () => {
    dispatch({ type: 'PREV_STEP' })
  }

  // CRÉATION SESSION APPWRITE
  const createSession = async (title = null) => {
    try {
      const sessionTitle = title || `Screening IA Expert ${new Date().toLocaleDateString()}`
      console.log('🆕 Création session Appwrite:', sessionTitle)
      
      // Vérifier Appwrite Auth
      if (!window.appwriteAuth || !window.appwriteAuth.isAuthenticated()) {
        throw new Error('Utilisateur non connecté à Appwrite')
      }
      
      const result = await window.appwriteAuth.createScreeningSession(
        sessionTitle,
        state.criteria,
        state.selectedAIModel
      )
      
      if (!result.success) {
        throw new Error(result.error)
      }
      
      const sessionId = result.session.$id
      dispatch({ type: 'SET_SESSION_ID', payload: sessionId })
      console.log('✅ Session Appwrite créée:', sessionId)
      
      return sessionId
    } catch (error) {
      console.error('❌ Erreur création session Appwrite:', error)
      throw error
    }
  }

  // UPLOAD FICHIERS AVEC APPWRITE
  const uploadFiles = async (files) => {
    try {
      console.log('📤 Upload Appwrite...', files.length, 'fichiers')
      
      let sessionId = state.currentSessionId
      if (!sessionId) {
        sessionId = await createSession()
      }

      const uploadedFiles = []
      let successCount = 0
      let errorCount = 0

      for (let i = 0; i < files.length; i++) {
        const file = files[i]
        
        try {
          // Créer l'enregistrement du fichier dans Appwrite
          const result = await window.appwriteAuth.createCvFile(
            sessionId,
            file.name,
            `/temp/${file.name}`, // Path temporaire
            file.size,
            file.type
          )
          
          if (result.success) {
            uploadedFiles.push({
              ...result.file,
              originalFile: file, // Garder référence au fichier original pour extraction
              id: result.file.$id
            })
            successCount++
            console.log(`✅ Fichier ${file.name} enregistré dans Appwrite`)
          } else {
            console.error(`❌ Erreur fichier ${file.name}:`, result.error)
            errorCount++
          }
        } catch (fileError) {
          console.error(`❌ Erreur fichier ${file.name}:`, fileError)
          errorCount++
        }
      }

      dispatch({ type: 'SET_UPLOADED_FILES', payload: uploadedFiles })
      console.log(`✅ Upload Appwrite terminé: ${successCount} succès, ${errorCount} erreurs`)
      
      return { 
        successCount, 
        errorCount,
        results: uploadedFiles
      }
    } catch (error) {
      console.error('❌ Erreur upload Appwrite:', error)
      throw error
    }
  }

  const addFiles = async (files) => {
    try {
      return await uploadFiles(files)
    } catch (error) {
      console.error('❌ Erreur addFiles Appwrite:', error)
      throw error
    }
  }

  // SUPPRESSION FICHIER APPWRITE
  const removeFile = async (fileId) => {
    try {
      console.log('🗑️ Suppression fichier Appwrite:', fileId)
      
      const result = await window.appwriteAuth.deleteCvFile(fileId)
      if (result.success) {
        dispatch({ type: 'REMOVE_FILE', payload: fileId })
        console.log('✅ Fichier supprimé d\'Appwrite')
      } else {
        throw new Error(result.error)
      }
    } catch (error) {
      console.error('❌ Erreur suppression Appwrite:', error)
      throw error
    }
  }

  const updateCriteria = (field, value) => {
    console.log('📝 IA Expert - Mise à jour critère:', field, '=', value)
    dispatch({ type: 'UPDATE_CRITERIA', field, value })
  }

  const setAIModel = (model) => {
    console.log('🧠 IA Expert - Modèle IA sélectionné:', model)
    dispatch({ type: 'SET_AI_MODEL', payload: model })
  }

  // EXTRACTION PDF IA Expert RENFORCÉE (identique à votre version)
  const extractTextFromPDF = async (fileBlob, fileName) => {
    try {
      console.log('📄 === EXTRACTION PDF IA Expert ===')
      console.log('🔍 Analyse:', fileName)
      
      const arrayBuffer = await fileBlob.arrayBuffer()
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise
      console.log('📖 PDF chargé:', pdf.numPages, 'pages')
      
      let fullText = ''
      let hasImages = false
      let imageCount = 0
      let imageDetails = []
      let photoDetected = false
      
      // DÉTECTION PHOTO ULTRA-RENFORCÉE
      for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
        const page = await pdf.getPage(pageNum)
        
        const textContent = await page.getTextContent()
        const pageText = textContent.items
          .map(item => item.str)
          .join(' ')
          .replace(/\s+/g, ' ')
          .trim()
        
        fullText += pageText + '\n'
        
        // MÉTHODE 1: Analyse des opérateurs PDF
        try {
          const operatorList = await page.getOperatorList()
          
          for (let i = 0; i < operatorList.fnArray.length; i++) {
            const operator = operatorList.fnArray[i]
            
            const imageOperators = [
              pdfjsLib.OPS.paintImageXObject,
              pdfjsLib.OPS.paintInlineImageXObject,
              pdfjsLib.OPS.paintImageMaskXObject,
              pdfjsLib.OPS.paintFormXObjectBegin
            ]
            
            if (imageOperators.includes(operator)) {
              hasImages = true
              photoDetected = true
              imageCount++
              
              const operatorName = getOperatorName(operator)
              imageDetails.push(`Page ${pageNum}: ${operatorName}`)
              console.log(`🖼️ IA Expert - IMAGE DÉTECTÉE page ${pageNum}: ${operatorName}`)
            }
          }
        } catch (opError) {
          console.log('⚠️ Erreur analyse opérateurs page', pageNum)
        }
        
        // MÉTHODE 2: Analyse des annotations
        try {
          const annotations = await page.getAnnotations()
          annotations.forEach((annotation, idx) => {
            if (annotation.subtype === 'Widget' || 
                annotation.hasAppearance || 
                annotation.subtype === 'Stamp') {
              hasImages = true
              photoDetected = true
              imageCount++
              imageDetails.push(`Page ${pageNum}: Annotation ${annotation.subtype}`)
              console.log(`🖼️ IA Expert - IMAGE via ANNOTATION page ${pageNum}`)
            }
          })
        } catch (annotError) {
          console.log('⚠️ Erreur analyse annotations page', pageNum)
        }
      }
      
      // MÉTHODE 3: Analyse taille fichier (heuristique IA Expert)
      const fileSize = fileBlob.size
      if (fileSize > 100000 && fullText.length < 2000) {
        hasImages = true
        photoDetected = true
        imageDetails.push('Heuristique IA Expert: Fichier volumineux avec peu de texte')
        console.log('🖼️ IA Expert - IMAGE suspectée via HEURISTIQUE')
      }
      
      // CONSTRUCTION SECTION PHOTO ULTRA-CLAIRE pour IA Expert
      let photoSection = ''
      if (hasImages || photoDetected || imageCount > 0) {
        photoSection = `=== INFORMATION PHOTO CRITIQUE ===
PHOTO_PRESENTE: OUI
NOMBRE_IMAGES: ${imageCount}
STATUT_PHOTO: PHOTO_DETECTEE_DANS_PDF
DETAILS_DETECTION: ${imageDetails.join(' | ')}
METHODES_MARIE_DUBOIS: Opérateurs PDF, Annotations, Heuristiques Expert RH
CONFIRMATION_FINALE: Le CV contient ${imageCount} image(s)/photo(s) détectée(s) par IA Expert
FIABILITE_DETECTION: HAUTE (${imageDetails.length} méthodes confirmées)
=== FIN INFORMATION PHOTO ===

`
        console.log(`🖼️ ✅ IA Expert - PHOTO CONFIRMÉE: ${imageCount} image(s)`)
      } else {
        photoSection = `=== INFORMATION PHOTO CRITIQUE ===
PHOTO_PRESENTE: NON
NOMBRE_IMAGES: 0
STATUT_PHOTO: AUCUNE_PHOTO_DETECTEE
DETAILS_DETECTION: Toutes méthodes IA Expert négatives
METHODES_MARIE_DUBOIS: Opérateurs PDF, Annotations, Heuristiques Expert RH
CONFIRMATION_FINALE: Le CV ne contient aucune photo selon IA Expert
FIABILITE_DETECTION: HAUTE (toutes méthodes confirmées négatives)
=== FIN INFORMATION PHOTO ===

`
        console.log('📷 ❌ IA Expert - AUCUNE PHOTO confirmée')
      }
      
      const finalText = photoSection + fullText
      
      console.log(`✅ Extraction IA Expert terminée: ${finalText.length} caractères`)
      console.log(`🖼️ RÉSULTAT PHOTO IA Expert: ${photoDetected ? 'PHOTOS DÉTECTÉES' : 'AUCUNE PHOTO'}`)
      
      if (finalText.length < 200) {
        throw new Error('PDF semble vide ou non textuel')
      }
      
      return finalText
      
    } catch (error) {
      console.error('❌ Erreur extraction PDF IA Expert:', error)
      throw error
    }
  }

  // Fonction utilitaire pour noms d'opérateurs
  const getOperatorName = (operator) => {
    const opNames = {
      [pdfjsLib.OPS.paintImageXObject]: 'paintImageXObject',
      [pdfjsLib.OPS.paintInlineImageXObject]: 'paintInlineImageXObject', 
      [pdfjsLib.OPS.paintImageMaskXObject]: 'paintImageMaskXObject',
      [pdfjsLib.OPS.paintFormXObjectBegin]: 'paintFormXObjectBegin'
    }
    return opNames[operator] || `Unknown_${operator}`
  }
 
  // ANALYSE AVEC IA Expert + APPWRITE
  const startAnalysis = async () => {
    try {
      console.log('🚀 === DÉMARRAGE ANALYSE IA Expert + Appwrite ===')
      
      const sessionFiles = state.uploadedFiles
      console.log('📁 Fichiers à analyser:', sessionFiles?.length || 0)
      
      if (!sessionFiles || sessionFiles.length === 0) {
        throw new Error('Aucun fichier à analyser')
      }
 
      // Vérifier Multi-AI API
      console.log('🔍 Vérification Multi-AI API...')
      if (!window.MultiAIAPI) {
        console.error('❌ window.MultiAIAPI non trouvée')
        throw new Error('Multi-AI API non chargée - vérifiez que le script multi-ai-api.js est chargé')
      }
      
      if (!window.multiAIAPI) {
        console.log('🔧 Création instance Multi-AI API...')
        window.multiAIAPI = new window.MultiAIAPI()
        console.log('✅ Multi-AI API créée')
      }
 
      // Afficher les critères actifs IA Expert
      const disqualifyingCriteria = Object.keys(state.criteria).filter(key => 
        key.startsWith('disqualifying_') && state.criteria[key]
      )
      console.log('🚨 IA Expert - Critères disqualifiants actifs:', disqualifyingCriteria.length)
      disqualifyingCriteria.forEach(criterion => {
        console.log(`   - ${criterion}: ${state.criteria[criterion]}`)
      })
 
      dispatch({ type: 'SET_ANALYZING', payload: true })
      dispatch({ type: 'SET_STEP', payload: 8 })
      dispatch({ type: 'UPDATE_PROGRESS', payload: { 
        current: 0, 
        total: sessionFiles.length,
        success: 0, 
        errors: 0 
      }})
 
      console.log('🧠 IA Expert - Modèle Expert sélectionné:', state.selectedAIModel)
      console.log('👩‍💼 Expert RH: IA Expert - 25 ans d\'expérience')
      console.log('📋 Critères professionnels IA Expert:', state.criteria)
 
      const results = []
      let successCount = 0
      let errorCount = 0
      let disqualifiedCount = 0
 
      // Analyser chaque fichier avec IA Expert + Appwrite
      for (let i = 0; i < sessionFiles.length; i++) {
        const file = sessionFiles[i]
        
        try {
          console.log(`📄 Analyse IA Expert ${i + 1}/${sessionFiles.length}: ${file.file_name}`)
          
          dispatch({ 
            type: 'UPDATE_PROGRESS', 
            payload: { 
              current: i, 
              total: sessionFiles.length, 
              success: successCount, 
              errors: errorCount 
            } 
          })
          
          const originalFile = file.originalFile
          if (!originalFile) {
            throw new Error('Fichier original non trouvé')
          }
          
          console.log('📁 Extraction PDF IA Expert avec détection photo renforcée...')
          const cvText = await extractTextFromPDF(originalFile, file.file_name)
          
          console.log('👩‍💼 Analyse IA Expert - Expert RH...')
          const analysis = await window.multiAIAPI.analyzeCVProfessional(
            cvText, 
            state.criteria, 
            state.selectedAIModel
          )
          
          console.log('📊 Analyse IA Expert terminée:')
          console.log(`   - Candidat: ${analysis.nom} ${analysis.prenom}`)
          console.log(`   - Score: ${analysis.score}/100`)
          console.log(`   - Statut: ${analysis.statut}`)
          console.log(`   - Photo: ${analysis.photo_presente ? 'OUI' : 'NON'}`)
          console.log(`   - Recommandation: ${analysis.recommandation_finale}`)

          // SAUVEGARDER L'ANALYSE DANS APPWRITE
          try {
            await window.appwriteAuth.updateCvFileAnalysis(
              file.id, // file.$id d'Appwrite
              cvText,
              analysis,
              analysis.score || 0
            )
            console.log(`💾 Analyse sauvée dans Appwrite pour ${file.file_name}`)
          } catch (saveError) {
            console.error('⚠️ Erreur sauvegarde Appwrite:', saveError)
          }
 
          results.push({
            ...analysis,
            fileName: file.file_name,
            fileId: file.id,
            resultId: `marie_dubois_${Date.now()}_${i}`,
            ai_model_used: state.selectedAIModel,
            expert_rh: 'IA Expert',
            analysisDate: new Date().toISOString(),
            originalFileSize: originalFile.size,
            extractedTextLength: cvText.length,
            criteriaUsed: state.criteria,
            profileSearched: state.criteria.selectedProfile,
            niveau_analyse: 'EXPERT_MARIE_DUBOIS'
          })
          
          if (analysis.score === 0) {
            disqualifiedCount++
            console.log(`❌ ${file.file_name} - DISQUALIFIÉ par IA Expert: ${analysis.criteres_disqualifiants?.details}`)
          } else {
            successCount++
            console.log(`✅ ${file.file_name} - Score IA Expert: ${analysis.score}/100 (${analysis.statut})`)
          }
 
        } catch (error) {
          errorCount++
          console.error(`❌ Erreur IA Expert ${file.file_name}:`, error)
          
          results.push({
            fileName: file.file_name,
            fileId: file.id,
            nom: file.file_name.replace(/\.[^/.]+$/, ""),
            prenom: '',
            score: 0,
            statut: 'ERREUR_ANALYSE',
            avis_expert_rh: `❌ ERREUR IA Expert: ${error.message}`,
            recommandation_finale: 'CV non analysable par expert RH IA Expert',
            decision_finale: 'REJET TECHNIQUE',
            ai_model_used: state.selectedAIModel,
            expert_rh: 'IA Expert',
            extraction_reussie: false,
            error: error.message,
            analysisDate: new Date().toISOString()
          })
        }
 
        dispatch({ 
          type: 'UPDATE_PROGRESS', 
          payload: { 
            current: i + 1,
            total: sessionFiles.length,
            success: successCount, 
            errors: errorCount 
          } 
        })
 
        // Délai IA Expert (réaliste)
        if (i < sessionFiles.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 1500))
        }
      }

      // METTRE À JOUR LA SESSION APPWRITE
      if (state.currentSessionId) {
        try {
          await window.appwriteAuth.updateScreeningSession(state.currentSessionId, {
            status: 'completed',
            completed_at: new Date().toISOString(),
            total_files: sessionFiles.length,
            processed_files: successCount + disqualifiedCount,
            average_score: results.reduce((sum, r) => sum + (r.score || 0), 0) / results.length || 0
          })
          console.log('💾 Session Appwrite mise à jour')
        } catch (updateError) {
          console.error('⚠️ Erreur mise à jour session:', updateError)
        }
      }
 
      // Tri par score décroissant
      results.sort((a, b) => (b.score || 0) - (a.score || 0))
 
      dispatch({ type: 'SET_RESULTS', payload: results })
      dispatch({ type: 'SET_STEP', payload: 9 })
      
      console.log('✅ === ANALYSE IA Expert + Appwrite TERMINÉE ===')
      console.log(`📊 Résultats finaux IA Expert:`)
      console.log(`   - ${successCount} candidats analysés`)
      console.log(`   - ${disqualifiedCount} candidats disqualifiés`)
      console.log(`   - ${errorCount} erreurs techniques`)
      console.log(`🏆 Meilleur candidat selon IA Expert: ${results[0]?.fileName} (Score: ${results[0]?.score}/100)`)
      console.log(`👩‍💼 Analyse effectuée par IA Expert, Directrice RH Expert`)
 
    } catch (error) {
      console.error('❌ Erreur analyse IA Expert + Appwrite:', error)
      dispatch({ type: 'SET_ANALYZING', payload: false })
      alert(`Erreur d'analyse IA Expert: ${error.message}`)
      throw error
    }
  }
 
  const resetScreening = () => {
    console.log('🔄 Reset screening IA Expert')
    dispatch({ type: 'RESET_SCREENING' })
  }
 
  const getCurrentSession = () => state.currentSessionId
  
  // RÉCUPÉRER FICHIERS DEPUIS APPWRITE
  const getSessionFiles = async () => {
    if (!state.currentSessionId) return []
    
    try {
      const result = await window.appwriteAuth.getCvFiles(state.currentSessionId)
      if (result.success) {
        return result.files
      }
      return []
    } catch (error) {
      console.error('❌ Erreur récupération fichiers Appwrite:', error)
      return []
    }
  }
 
  // RÉCUPÉRER SESSION DEPUIS APPWRITE
  const getSessionData = async () => {
    if (!state.currentSessionId) return null
    
    try {
      const result = await window.appwriteAuth.getScreeningSession(state.currentSessionId)
      if (result.success) {
        return result.session
      }
      return null
    } catch (error) {
      console.error('❌ Erreur récupération session Appwrite:', error)
      return null
    }
  }
 
  // RÉCUPÉRER TOUTES LES SESSIONS DEPUIS APPWRITE
  const getAllSessions = async () => {
    try {
      const result = await window.appwriteAuth.getScreeningSessions()
      if (result.success) {
        return result.sessions
      }
      return []
    } catch (error) {
      console.error('❌ Erreur récupération sessions Appwrite:', error)
      return []
    }
  }
 
  // Exposer extractTextFromPDF globalement pour IA Expert
  window.extractTextFromPDF = extractTextFromPDF
 
  return (
    <ScreeningContext.Provider value={{
      // État
      ...state,
      
      // Navigation
      setStep,
      nextStep,
      prevStep,
      
      // Sessions IA Expert + Appwrite
      createSession,
      getCurrentSession,
      getSessionFiles,
      getSessionData,
      getAllSessions,
      
      // Fichiers
      uploadFiles,
      addFiles,
      removeFile,
      
      // Configuration
      updateCriteria,
      setAIModel,
      
      // Analyse IA Expert + Appwrite
      startAnalysis,
      resetScreening
    }}>
      {children}
    </ScreeningContext.Provider>
  )
 }
 
 export const useScreening = () => {
  const context = useContext(ScreeningContext)
  if (context === undefined) {
    throw new Error('useScreening must be used within a ScreeningProvider')
  }
  return context
 }